import React, { useEffect, useRef, useState } from "react"
import { destroyCookie, parseCookies } from "nookies"
import { LOGIN_PAGE_ROUTE } from "@pma/utils/page-routes"
import { useNavigate } from "react-router-dom"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import axios from "axios"
import { GET_ORGANIZATION, ME_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { useQuery } from "@tanstack/react-query"
import LoginForm from "./login-form"
import { Drawer, DrawerContent, DrawerHeader } from "../common/drawer"
import ROGBackground from "../layout/rog-background"
import { useWarnings } from "@pma/contexts/WarningsContext"

interface Props {
  children?: React.ReactNode
}
const WithAuth = ({ children }: Props) => {
  const [isClient, setIsClient] = useState(false)
  const [count, setCount] = useState(45)
  const countRef = useRef(count)
  const { setWarnings, isRefetchWarning } = useWarnings()

  const { authConfig, signIn } = useAuth()

  const { translate } = useTranslator()

  const name = authConfig?.name
  const email = authConfig?.email
  const orgId = authConfig?.last_organization_id

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const hasConfig = !!authConfig?.id

  let message

  const navigate = useNavigate()

  const getMeDetails = async (accessToken: string) => {
    const response = await axios.get(SERVER_URL + ME_ENDPOINT, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const signOut = () => {
    destroyCookie(null, ACCESS_TOKEN)
    !parseCookies()[ACCESS_TOKEN] && navigate(LOGIN_PAGE_ROUTE)
  }

  const {
    data: userDetails,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["me-details", orgId, authConfig?.id, accessToken],
    queryFn: () => getMeDetails(accessToken),
    enabled: !!accessToken,
  })

  const workSpaceDetails = async () => {
    const response = await axios.get(SERVER_URL + GET_ORGANIZATION + `/${orgId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const { data: organization } = useQuery({
    queryKey: ["workspace-details", userDetails?.id, isRefetchWarning],
    queryFn: workSpaceDetails,
    enabled: userDetails?.id ? true : false, // Only fetch if orgId exists and hasn't been fetched before
  })

  useEffect(() => {
    if (organization?.show_warnings) {
      setWarnings(organization?.show_warnings)
    }
  }, [organization])
  const refreshSession = (!accessToken && hasConfig) || isError

  useEffect(() => {
    countRef.current = count
  }, [count])

  useEffect(() => {
    setIsClient(true)
    if (!accessToken && !hasConfig) {
      message = translate("you are not logged in")

      signOut()
      return navigate(LOGIN_PAGE_ROUTE)
    }
  }, [!!accessToken, !!hasConfig])

  useEffect(() => {
    let timer: NodeJS.Timeout
    // Give user 45 seconds to refresh their session else logout
    if (refreshSession) {
      setTimeout(() => {
        signOut()
        return navigate(LOGIN_PAGE_ROUTE)
      }, 45000)
    }
  }, [refreshSession])

  useEffect(() => {
    if (!isFetching) signIn({ ...userDetails })
  }, [isFetching])

  if (!isClient) {
    return null
  }

  if (refreshSession) {
    return (
      <div className='h-[100vh] overflow-hidden'>
        <Drawer open dismissible={false}>
          <DrawerContent className='h-[80vh] bg-transparent py-4'>
            <ROGBackground>
              <div className='z-50 w-full rounded-sm p-4 text-white md:w-[400px]'>
                <DrawerHeader className=''>
                  <div className='flex gap-1'>
                    <h4>
                      {translate("Hi")}, {name}
                    </h4>
                  </div>
                  <p className='mb-2 text-sm'>
                    {translate("Please enter your password to refresh your session")}.
                  </p>
                </DrawerHeader>
                <LoginForm email={email || ""} />
              </div>{" "}
            </ROGBackground>
          </DrawerContent>
        </Drawer>
        <div className='-z-50' onClick={(e) => e.preventDefault()}>
          {children}
        </div>{" "}
      </div>
    )
  }

  return <>{children}</>
}

export default WithAuth
